/**
 * SeriesPageRenderer - Canonical Page Builder Renderer for Series Detail Pages
 * Location: /app/frontend/src/components/page-builder/series/
 * 
 * Features:
 * - Fetches page definition from Page Builder API
 * - Pre-flight entity check: verifies series exists before rendering blocks
 * - AbortController with 10s timeout prevents infinite loading
 * - Distinct Not Found vs Server Error UI
 * - Admin-only debug flag for legacy renderer (default OFF)
 * - Feature flag: USE_CANONICAL_SERIES_DETAIL in App.js
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle, BookOpen, Construction, Home, ArrowLeft, FileX } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useRouteParams, useLegacyRendererDebug } from '../../CanonicalRouteProvider';
import { apiClient } from '../../../config/api';

// Import series section renderers
import {
  SeriesHeroSection,
  EpisodeListSection,
  SeriesInfoPanelSection,
  SeriesCommentsSection,
  RelatedSeriesSection,
  SeriesCreatorSpotlightSection,
  SeriesCTASection,
} from './SeriesSectionRenderers';

// Import legacy SeriesDetail only for admin debug mode
import SeriesDetail from '../../../pages/SeriesDetail';
import AdminDebugPill from '../AdminDebugPill';

const FETCH_TIMEOUT_MS = 10000;

// Section type to component mapping
const SERIES_SECTION_RENDERERS = {
  section_series_hero: SeriesHeroSection,
  section_episode_list: EpisodeListSection,
  section_series_info: SeriesInfoPanelSection,
  section_series_comments: SeriesCommentsSection,
  section_related_series: RelatedSeriesSection,
  section_series_creator: SeriesCreatorSpotlightSection,
  section_series_cta: SeriesCTASection,
  series_hero: SeriesHeroSection,
  episode_list: EpisodeListSection,
  series_info_panel: SeriesInfoPanelSection,
  series_comments: SeriesCommentsSection,
  related_series: RelatedSeriesSection,
  series_creator_spotlight: SeriesCreatorSpotlightSection,
  series_cta: SeriesCTASection,
};

/** Loading state */
const PageLoading = () => (
  <div className="min-h-screen bg-black flex items-center justify-center" data-testid="series-page-loading">
    <div className="text-center">
      <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
      <p className="text-zinc-400">Loading series...</p>
    </div>
  </div>
);

/** Not Found state - for invalid/missing series */
const PageNotFound = ({ seriesId }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="series-not-found">
    <div className="max-w-md text-center">
      <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
        <FileX className="w-8 h-8 text-zinc-500" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Series Not Found</h1>
      <p className="text-zinc-400 mb-6">
        {seriesId
          ? `No series exists with ID "${seriesId.slice(0, 12)}..."`
          : 'No series ID was provided.'}
      </p>
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          className="border-zinc-700 text-zinc-300"
          data-testid="series-not-found-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
        <Link to="/comics">
          <Button className="bg-orange-500 hover:bg-orange-600" data-testid="series-not-found-browse-btn">
            <BookOpen className="w-4 h-4 mr-2" />
            Browse Comics
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

/** Error state - for server / timeout errors */
const PageError = ({ message, onRetry }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="series-page-error">
    <div className="max-w-md text-center">
      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Failed to Load Page</h1>
      <p className="text-zinc-400 mb-6">{message || 'Something went wrong while loading this page.'}</p>
      <div className="flex gap-3 justify-center">
        {onRetry && (
          <Button onClick={onRetry} className="bg-orange-500 hover:bg-orange-600" data-testid="series-error-retry-btn">
            Try Again
          </Button>
        )}
        <Link to="/">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" data-testid="series-error-home-btn">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

/** Safe default template when no Page Builder definition exists */
const SafeDefaultTemplate = ({ seriesId, isAdmin }) => (
  <div className="min-h-screen bg-black">
    <SeriesHeroSection seriesId={seriesId} block={{ props: {} }} />
    <SeriesInfoPanelSection seriesId={seriesId} block={{ props: {} }} />
    <EpisodeListSection seriesId={seriesId} block={{ props: {} }} />
    {isAdmin && (
      <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mx-6 my-4 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-500">
          <Construction className="w-5 h-5" />
          <span className="font-medium">Admin Notice</span>
        </div>
        <p className="text-yellow-400/80 text-sm mt-2">
          This series page is using the default template. Customize it in Page Builder.
        </p>
      </div>
    )}
    <SeriesCommentsSection seriesId={seriesId} block={{ props: {} }} />
  </div>
);

/** SeriesPageRenderer - Main component */
const SeriesPageRenderer = () => {
  const { seriesId } = useParams();
  const routeParams = useRouteParams();
  const { legacyEnabled, isAdmin: isAdminDebug } = useLegacyRendererDebug();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const abortRef = useRef(null);

  const effectiveSeriesId = seriesId || routeParams?.seriesId;

  // Check if user is admin
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setIsAdmin(user?.role === 'admin' || user?.is_admin);
      } catch (e) {
        setIsAdmin(false);
      }
    }
  }, []);

  // Pre-flight entity check + fetch page definition
  useEffect(() => {
    // Abort any previous in-flight request
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    const timedOut = { current: false };
    const timeoutId = setTimeout(() => {
      timedOut.current = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchData = async () => {
      if (!effectiveSeriesId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        console.log('[SeriesPageRenderer] Canonical rendering for series:', effectiveSeriesId);

        // Pre-flight: verify the series exists
        const entityCheck = await apiClient.get(`/series/${effectiveSeriesId}`, {
          signal: controller.signal,
        });
        if (!entityCheck.data || entityCheck.data.error) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Fetch page definition from Page Builder API
        const response = await apiClient.get('/page-builder/render/series_detail', {
          signal: controller.signal,
        });
        setPageData(response.data);
      } catch (err) {
        if (controller.signal.aborted) {
          if (timedOut.current) {
            setError('Request timed out. Please check your connection and try again.');
          }
          // Cleanup abort — do nothing, let the next mount handle it
          setLoading(false);
          return;
        }
        if (err.response?.status === 404) {
          // Check which request 404'd
          if (err.config?.url?.includes(`/series/${effectiveSeriesId}`)) {
            setNotFound(true);
          } else {
            // Page definition not found — use defaults
            setPageData({ page_id: 'series_detail', blocks: [] });
          }
        } else {
          console.error('[SeriesPageRenderer] Error:', err);
          setError(err.message || 'Failed to load page');
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [effectiveSeriesId]);

  // Admin debug: use legacy renderer
  if (legacyEnabled) {
    return (
      <>
        <SeriesDetail />
        <AdminDebugPill />
      </>
    );
  }

  if (loading) return <PageLoading />;
  if (notFound) return <PageNotFound seriesId={effectiveSeriesId} />;
  if (error) return <PageError message={error} onRetry={() => window.location.reload()} />;

  // No page data or empty blocks - use safe default
  if (!pageData || !pageData.blocks || pageData.blocks.length === 0) {
    return (
      <>
        <SafeDefaultTemplate seriesId={effectiveSeriesId} isAdmin={isAdmin} />
        <AdminDebugPill />
      </>
    );
  }

  // Render page via Page Builder blocks
  const enabledBlocks = pageData.blocks
    .filter(block => block.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div
      className="min-h-screen bg-black"
      data-testid="series-page-renderer"
      data-series-id={effectiveSeriesId}
    >
      {/* Canonical rendering badge (admin only) */}
      {isAdmin && (
        <div className="bg-green-500/10 border-b border-green-500/30 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500/20 text-green-400 text-xs">
                Canonical PageBuilder Renderer
              </Badge>
              <span className="text-green-400/60 text-xs">
                Page ID: {pageData.page_id} | Blocks: {enabledBlocks.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Render each block using series section renderers */}
      {enabledBlocks.map((block) => {
        const type = block._templateId || block.type;
        const Renderer = SERIES_SECTION_RENDERERS[type];

        if (!Renderer) {
          console.warn('[SeriesPageRenderer] Unknown block type:', type);
          return (
            <div key={block.id} className="py-4 px-6 bg-zinc-900/50 border border-zinc-800 my-2 mx-4 rounded">
              <p className="text-zinc-500 text-sm">Unknown section type: {type}</p>
            </div>
          );
        }

        return (
          <Renderer
            key={block.id}
            block={block}
            seriesId={effectiveSeriesId}
            isPreview={false}
          />
        );
      })}

      {/* Admin debug pill (only for admins) */}
      <AdminDebugPill />
    </div>
  );
};

export default SeriesPageRenderer;
